"use client";

import Lottie from "lottie-react";
import { useEffect, useRef, useState } from "react";

const sections = [
  {
    icon: "✍️",
    label: "Ad Copy Writer",
    title: "Write copy that converts across every platform",
    description:
      "From hooks to headlines, generate platform-specific ad copy designed to capture attention, improve CTR, and drive consistent performance.",
    animation: "/assets/adCreation/ad-copy-Playful.json",
  },
  {
    icon: "🎬",
    label: "Video Ad Maker",
    title: "Turn ideas into scroll-stopping video ads",
    description:
      "Create short-form video ads optimized for social — built to engage faster, retain attention, and increase conversions across channels.",
    animation: "/assets/adCreation/video-generate-Playful.json",
  },
  {
    icon: "🖼️",
    label: "Image Generator",
    title: "Design visuals that align with performance, not guesswork",
    description:
      "Generate high-quality ad creatives tailored to formats and audiences — helping you produce visuals that resonate and convert.",
    animation: "/assets/adCreation/Image-Generate.json",
  },
  {
    icon: "📦",
    label: "Bulk Ad Maker",
    title: "Launch 50+ ad variations in minutes",
    description:
      "Scale your campaigns effortlessly by generating multiple ad creatives at once — so you can test more angles and find winners faster.",
    animation: "/assets/adCreation/Bulk-Ad.json",
  },
  {
    icon: "📐",
    label: "Smart Resizing",
    title: "Adapt every creative to every format instantly",
    description:
      "Automatically resize and optimise your ads for all platforms — ensuring consistency, speed, and zero manual effort.",
    animation: "/assets/adCreation/Smart-resize%20(1).json",
  },
];

// Each section loads its own Lottie and plays it when scrolled into view.
const SectionVisual = ({ animation }) => {
  const [animationData, setAnimationData] = useState(null);
  const containerRef = useRef(null);
  const lottieRef = useRef(null);

  useEffect(() => {
    fetch(animation)
      .then((res) => res.json())
      .then((data) => setAnimationData(data));
  }, [animation]);

  useEffect(() => {
    if (!animationData) return;
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        lottieRef.current?.goToAndPlay(0, true);
      },
      { threshold: 0.4 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [animationData]);

  return (
    <div className="flex w-full justify-center" ref={containerRef}>
      {animationData && (
        <Lottie
          lottieRef={lottieRef}
          animationData={animationData}
          loop={false}
          autoplay={false}
        />
      )}
    </div>
  );
};

const MoreVariations = () => {
  return (
    <section className="bg-white">
      {sections.map((item) => (
        <div
          key={item.label}
          className="sticky top-0 flex min-h-screen items-center bg-white px-5 sm:px-6 lg:px-10"
        >
          <div className="mx-auto grid w-full max-w-full grid-cols-1 items-center justify-items-center gap-10 lg:grid-cols-2">
            {/* ── Copy (left) ── */}
            <div className="order-2 text-center lg:order-1 lg:text-left">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-3 py-1 text-[11px] font-medium tracking-wide text-[#292929]">
                <span className="text-sm leading-none">{item.icon}</span>
                <span className="font-kalam">{item.label}</span>
              </div>

              <h2 className="solution-sub-heading mx-auto max-w-[550px] 2xl:max-w-[700px] 3xl:max-w-[800px] lg:mx-0">
                {item.title}
              </h2>

              <p className="mt-4 mx-auto max-w-[450px] 2xl:max-w-[500px] 3xl:max-w-[600px] body-secondary-text lg:mx-0">
                {item.description}
              </p>
            </div>

            {/* ── Visual (right) ── */}
            <div className="order-1 w-full lg:order-2">
              <SectionVisual animation={item.animation} />
            </div>
          </div>
        </div>
      ))}
    </section>
  );
};

export default MoreVariations;
